import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

const control =
  "w-full rounded-xl border border-hairline-strong bg-surface px-3.5 py-2.5 text-sm " +
  "text-content placeholder:text-content-subtle transition-colors " +
  "hover:border-hairline-strong disabled:cursor-not-allowed disabled:opacity-60 " +
  "aria-[invalid=true]:border-critical-500 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-critical-500/20";

function Wrapper({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label?: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-sm font-semibold">
          {label}
          {required && (
            <span className="text-critical-500 ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      {children}
      {/* Hint is hidden once an error replaces it, so the two never stack and
          push the form around as the user types. */}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-critical-500 text-xs font-medium">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-content-subtle text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

type FieldMeta = { label?: string; hint?: ReactNode; error?: string };

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & FieldMeta
>(function Input({ label, hint, error, className, id, ...props }, ref) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Wrapper id={fieldId} label={label} hint={hint} error={error} required={props.required}>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cn(control, className)}
        {...props}
      />
    </Wrapper>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & FieldMeta
>(function Textarea({ label, hint, error, className, id, ...props }, ref) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Wrapper id={fieldId} label={label} hint={hint} error={error} required={props.required}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cn(control, "min-h-24 resize-y", className)}
        {...props}
      />
    </Wrapper>
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & FieldMeta
>(function Select({ label, hint, error, className, id, children, ...props }, ref) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Wrapper id={fieldId} label={label} hint={hint} error={error} required={props.required}>
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cn(control, "appearance-none pr-9", className)}
        {...props}
      >
        {children}
      </select>
    </Wrapper>
  );
});
